// Application-layer frames for the pair / remote-control ceremonies.
//
// These travel inside `e2e:enc` payloads — the relay never sees them in
// cleartext. The shape is the contract between the desktop remote-control
// service and the browser/RN client. They live in this package so both
// peers can import the same schemas; the channel-level protocol below them
// is unchanged.
//
// The browser drives. The hello frame's `mode` discriminates between the
// two ceremonies the desktop supports on the same channel:
//
//   register      first-time pairing — browser displays a 6-digit code form,
//                 desktop responds with a registration challenge, browser
//                 returns a WebAuthn attestation, desktop persists the
//                 credential.
//
//   authenticate  returning paired browser — desktop responds with an
//                 authentication challenge, browser returns a WebAuthn
//                 assertion, desktop verifies against the paired-device
//                 registry, updates the sign counter, and accepts.
//
// Ceremony shapes:
//
//   register:
//     1. browser → desktop  pair:hello {mode:'register', pairingCode, proposedLabel}
//     2. desktop → browser  pair:registration-challenge
//     3. browser → desktop  pair:registration-response   attestation
//     4. desktop → browser  pair:registered | pair:rejected
//
//   authenticate:
//     1. browser → desktop  pair:hello {mode:'authenticate'}
//     2. desktop → browser  pair:auth-challenge
//     3. browser → desktop  pair:auth-response           assertion
//     4. desktop → browser  pair:authenticated | pair:rejected
//
// On any terminal frame the desktop ends the current ceremony Peer (without
// closing the underlying relay socket — see `RemoteControlService` in the
// desktop) so the next browser can begin a fresh ECDH session.

import { z } from 'zod';

export const PAIR_PROTOCOL_VERSION = 1;

const PairHelloRegisterSchema = z
  .object({
    type: z.literal('pair:hello'),
    protocolVersion: z.literal(PAIR_PROTOCOL_VERSION),
    mode: z.literal('register'),
    /** Short human-readable code displayed on the desktop UI. */
    pairingCode: z.string().min(1).max(32),
    /** User-supplied or UA-derived label, e.g. "Chrome on macOS". */
    proposedLabel: z.string().min(1).max(120),
  })
  .strict();

const PairHelloAuthenticateSchema = z
  .object({
    type: z.literal('pair:hello'),
    protocolVersion: z.literal(PAIR_PROTOCOL_VERSION),
    mode: z.literal('authenticate'),
  })
  .strict();

export const PairHelloSchema = z.discriminatedUnion('mode', [
  PairHelloRegisterSchema,
  PairHelloAuthenticateSchema,
]);

export const PairRegistrationChallengeSchema = z
  .object({
    type: z.literal('pair:registration-challenge'),
    /** base64url WebAuthn challenge (32 random bytes). */
    challenge: z.string().min(1).max(256),
    /** WebAuthn relying party id — registrable suffix of the page origin. */
    rpId: z.string().min(1).max(253),
    /** RP display name, e.g. "Baton". */
    rpName: z.string().min(1).max(120),
    /**
     * The exact origin the desktop will accept in `clientDataJSON.origin`.
     * Sent so the browser can sanity-check before invoking the authenticator.
     */
    origin: z.string().min(1).max(2048),
    user: z
      .object({
        /** base64url stable per-install id (so the same install dedupes passkeys). */
        id: z.string().min(1).max(128),
        name: z.string().min(1).max(120),
        displayName: z.string().min(1).max(120),
      })
      .strict(),
    /**
     * base64url credential ids the authenticator should refuse to re-register.
     * Cap is 256 so a user with many paired devices still fits in one
     * challenge frame at realistic ~64-byte credential ids (256 × ~86 base64url
     * chars ≈ 22 KB, comfortably under the 64 KB e2e frame budget).
     */
    excludeCredentialIds: z.array(z.string().min(1).max(512)).max(256),
  })
  .strict();

// SimpleWebAuthn's RegistrationResponseJSON contract — we accept the fields
// the server library reads, plus pass-through for anything else the spec
// adds. Keeping this loose intentionally: the deep validation lives in
// `verifyRegistrationResponse`, and re-deriving its checks here would be a
// drift hazard.
const AttestationResponseSchema = z
  .object({
    id: z.string().min(1).max(1024),
    rawId: z.string().min(1).max(1024),
    type: z.literal('public-key'),
    response: z
      .object({
        clientDataJSON: z.string().min(1),
        attestationObject: z.string().min(1),
      })
      .passthrough(),
    authenticatorAttachment: z.string().optional(),
    clientExtensionResults: z.record(z.unknown()).optional(),
  })
  .passthrough();

export const PairRegistrationResponseSchema = z
  .object({
    type: z.literal('pair:registration-response'),
    attestationResponse: AttestationResponseSchema,
  })
  .strict();

export const PairRegisteredSchema = z
  .object({
    type: z.literal('pair:registered'),
    /** base64url credential id the desktop persisted. */
    credentialId: z.string().min(1).max(1024),
    /** Final label as persisted (may differ from the proposedLabel if normalized). */
    label: z.string().min(1).max(120),
  })
  .strict();

// SimpleWebAuthn's AuthenticationResponseJSON — same passthrough strategy as
// the attestation shape above.
const AuthenticationResponseSchema = z
  .object({
    id: z.string().min(1).max(1024),
    rawId: z.string().min(1).max(1024),
    type: z.literal('public-key'),
    response: z
      .object({
        clientDataJSON: z.string().min(1),
        authenticatorData: z.string().min(1),
        signature: z.string().min(1),
        // userHandle is optional in the spec; we use it on the *browser* side
        // (to recover the install fingerprint pre-handshake) and don't
        // re-validate it here — the credential-id lookup is the trust anchor.
        userHandle: z.string().optional(),
      })
      .passthrough(),
    authenticatorAttachment: z.string().optional(),
    clientExtensionResults: z.record(z.unknown()).optional(),
  })
  .passthrough();

export const PairAuthChallengeSchema = z
  .object({
    type: z.literal('pair:auth-challenge'),
    /** base64url WebAuthn challenge (32 random bytes). */
    challenge: z.string().min(1).max(256),
    /** WebAuthn relying party id — registrable suffix of the page origin. */
    rpId: z.string().min(1).max(253),
    /** Exact origin the desktop will accept in `clientDataJSON.origin`. */
    origin: z.string().min(1).max(2048),
  })
  .strict();

export const PairAuthResponseSchema = z
  .object({
    type: z.literal('pair:auth-response'),
    authenticationResponse: AuthenticationResponseSchema,
  })
  .strict();

export const PairAuthenticatedSchema = z
  .object({
    type: z.literal('pair:authenticated'),
    /** base64url credential id the desktop authenticated against. */
    credentialId: z.string().min(1).max(1024),
    /** Friendly label of the device, looked up from the registry. */
    label: z.string().min(1).max(120),
  })
  .strict();

// Desktop-emitted failure. `reason` is a stable machine-readable token that
// callers can branch on; `message` is human-readable for display.
export const PairRejectedSchema = z
  .object({
    type: z.literal('pair:rejected'),
    reason: z.enum([
      // Register flow
      'wrong-code',
      'unsupported-mode',
      'attestation-invalid',
      'duplicate-credential',
      'protocol-version-mismatch',
      // Auth flow
      'unknown-credential',
      'signature-failed',
      'counter-regression',
      'not-paired',
      // Either
      'internal',
    ]),
    message: z.string().min(1).max(500),
  })
  .strict();

// Outer schema is a plain union — `pair:hello` is itself a discriminated
// union over `mode`, and Zod doesn't allow a discriminator's leaves to share
// a value. The performance cost (try-each on parse) is negligible here:
// every reachable shape rejects on first-mismatched-key in O(1) and frame
// parses are not in a hot path.
export const PairFrameSchema = z.union([
  PairHelloSchema,
  PairRegistrationChallengeSchema,
  PairRegistrationResponseSchema,
  PairRegisteredSchema,
  PairAuthChallengeSchema,
  PairAuthResponseSchema,
  PairAuthenticatedSchema,
  PairRejectedSchema,
]);

export type PairHello = z.infer<typeof PairHelloSchema>;
export type PairHelloRegister = z.infer<typeof PairHelloRegisterSchema>;
export type PairHelloAuthenticate = z.infer<typeof PairHelloAuthenticateSchema>;
export type PairRegistrationChallenge = z.infer<typeof PairRegistrationChallengeSchema>;
export type PairRegistrationResponse = z.infer<typeof PairRegistrationResponseSchema>;
export type PairRegistered = z.infer<typeof PairRegisteredSchema>;
export type PairAuthChallenge = z.infer<typeof PairAuthChallengeSchema>;
export type PairAuthResponse = z.infer<typeof PairAuthResponseSchema>;
export type PairAuthenticated = z.infer<typeof PairAuthenticatedSchema>;
export type PairRejected = z.infer<typeof PairRejectedSchema>;
export type PairFrame = z.infer<typeof PairFrameSchema>;

/**
 * Parse the JSON value carried inside a `e2e:enc` PAYLOAD_JSON frame into a
 * typed pair-ceremony frame, or `null` if it doesn't match any known shape.
 */
export function parsePairFrame(value: unknown): PairFrame | null {
  const result = PairFrameSchema.safeParse(value);
  return result.success ? result.data : null;
}
